import initAuth from "../utils/initAuth";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/auth";
import { useRouter } from "next/router";
import React, { useState, useEffect } from "react";
import { XCircleIcon } from "@heroicons/react/20/solid";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import getAbsoluteURL from "../utils/getAbsoluteURL";
import Link from "next/link";
import { useRegistration } from '../components/RegistrationContext'; 
import { useOrg } from "../components/OrgContext";
import LayoutRegister from '../components/LayoutRegister';
import { useUser } from 'next-firebase-auth';

initAuth();

const schema = yup.object({
  email: yup.string().email().required(),
  password: yup.string().required(),
}).required();

function RegPage() {
  const { setRegistrationInProgress, registrationInProgress } = useRegistration();
  const [isValid, setIsValid] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { updateActiveOrg } = useOrg();
  const AuthUser = useUser();
  const router = useRouter();
  const { email } = router.query;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
    defaultValues: {
      email: email || "", // Pre-populate if email is in the query
      password: "",
    },
  });

  const onSubmit = async (formData) => {
    setRegistrationInProgress(true);
    setSubmitting(true);
    try {
      const userCredential = await firebase.auth().createUserWithEmailAndPassword(formData.email, formData.password);
      const token = await userCredential.user.getIdToken();

      const data = {
        email: formData.email,
        password: formData.password,
        token: token,
      };

      const JSONdata = JSON.stringify(data);
      const endpoint = getAbsoluteURL("/api/register");

      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${token}`,
        },
        body: JSONdata,
      };

      const response = await fetch(endpoint, options);
      const result = await response.json();
      console.log('Register result:', result);

      // Update organization state and complete registration
      const org = {
        name: data.email,
        orgId: result.orgId,
        parentOrgId: result.orgId,
        primaryOrg: true,
      };
      updateActiveOrg(result, true);
      setRegistrationInProgress(false);
      
      gtag('event', 'conversion', {'send_to': 'AW-783295803/iAN4CJvq7eQZELvKwPUC'});
      // Redirect after successful registration
      // router.push(`/org/${org.orgId}/website`);
    } catch (error) {
      console.log('Error registering:', error);
      setIsValid(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LayoutRegister>
      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-12 w-auto"
            src="https://ranktivity-assets.imgix.net/assets/ranktivity-logo-dark.png?w=200&auto=compress"
            alt="Ranktivity"
          />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Create your Ranktivity account
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    {...register("email")}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {errors.email?.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    {...register("password")}
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600" id="password-error">
                    {errors.password?.message}
                  </p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md border border-transparent bg-secondary-300 py-2 px-4 text-sm font-medium text-white hover:bg-primary-300 focus:outline-none"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Sign Up"
                  )}
                </button>
              </div>

              <div className="flex items-center justify-center">
                <div className="text-sm">
                  <p className="font-medium text-gray-400">
                    Have an account?{" "}
                    <Link href="/login" shallow={true} legacyBehavior>
                      <a className="font-medium text-gray-400 hover:text-indigo-500">
                        Sign in
                      </a>
                    </Link>
                  </p>
                </div>
              </div>

              {!isValid && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>There was an error registering. The email is already in use by another account.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </LayoutRegister>
  );
}

export default RegPage;
